'use client'

import { ReactNode, useEffect, useState, Suspense } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import { useUser } from '@/hooks/useUser'
import { useAdminData } from '@/hooks/useAdminData'
import { AdminTab } from '@/types/admin'
import UserManagement from '@/components/admin/UserManagement'
import PropertyManagement from '@/components/admin/PropertyManagement'
// import ApplicationManagement from '@/components/admin/ApplicationManagement' // Unused
import RentalApplicationManagement from '@/components/admin/RentalApplicationManagement'
import EmailManagement from '@/components/admin/EmailManagement'
import PlatformAnalytics from '@/components/admin/PlatformAnalytics'
import TransactionManagement from '@/components/admin/TransactionManagement'
import CommissionManagement from '@/components/admin/CommissionManagement'
import PayoutManagement from '@/components/admin/PayoutManagement'
import DisputeResolution from '@/components/admin/DisputeResolution'
import ContentModeration from '@/components/admin/ContentModeration'
import ModerationViolations from '@/components/admin/ModerationViolations'
import PlatformSettings from '@/components/admin/PlatformSettings'
import AuditLogs from '@/components/admin/AuditLogs'
import BulkOperations from '@/components/admin/BulkOperations'
import AdminNotificationSystem from '@/components/admin/AdminNotificationSystem'
import ReviewsManagement from '@/components/admin/ReviewsManagement'
import KycVerificationWorkflow from '@/components/admin/KycVerificationWorkflow'

function AdminDashboardContent() {
  const [activeTab, setActiveTab] = useState<AdminTab>('overview')
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  const navigate = useNavigate()
  const { logout } = useAuth()
  const { user, loading, isAdmin } = useUser()

  // Redirect if not admin
  useEffect(() => {
    if (!loading && (!user || !isAdmin)) {
      navigate('/auth')
    }
  }, [loading, user, isAdmin, navigate])

  // Get user ID - handle both id and _id for compatibility
  const userId = user?.id || (user as any)?._id || ''

  // Get data using custom hook
  // The hook automatically loads data when userId changes
  const {
    users,
    properties,
    applications,
    payments,
    error: dataError,
    stats,
    refreshAll
  } = useAdminData({
    userId
  })

  const handleLogout = () => {
    // Use centralized logout from AuthContext
    logout()
  }

  const handleDataUpdate = () => {
    refreshAll()
  } 

  // Use centralized stats
  const {
    totalUsers,
    totalProperties,
    verifiedProperties,
    pendingProperties,
    kycApplications,
    pendingKycApplications,
    approvedKycApplications,
    verifiedUsers,
    unverifiedUsers,
    landlords,
    clients,
    totalRevenue,
    platformCommission,
    verificationRate,
    propertyVerificationRate
  } = stats

  /** Format money for stat cards: ₦2M, ₦100K, or full amount below 1K */
  const formatMoneyShort = (n: number): string => {
    if (n >= 1_000_000) {
      const m = n / 1_000_000
      return `₦${m % 1 === 0 ? m : Number(m.toFixed(1))}M`
    }
    if (n >= 1_000) {
      const k = n / 1_000
      return `₦${k % 1 === 0 ? k : Number(k.toFixed(1))}K`
    }
    return `₦${n.toLocaleString()}`
  }

  type TabItem = {
    key: AdminTab
    label: string
    icon: ReactNode
    hint?: string
    badge?: number
  }

  const tabItems: TabItem[] = [
    {
      key: 'overview',
      label: 'Overview',
      icon: (
        <img src="/logo.png" alt="Logo" className="w-6 h-6 object-contain" />
      ),
      hint: 'Control centre'
    },
    {
      key: 'users',
      label: 'Users',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2a5.002 5.002 0 00-9.9-1M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2a5.002 5.002 0 019.9-1M9 7a4 4 0 11-8 0 4 4 0 018 0zm14 0a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      ),
      hint: 'Onboard & verify',
      badge: unverifiedUsers
    },
    {
      key: 'properties',
      label: 'Properties',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 9.75L12 4l9 5.75M4.5 10.5V20h15v-9.5M9 20v-6h6v6" />
        </svg>
      ),
      hint: 'Listings & review',
      badge: pendingProperties
    },
    {
      key: 'applications',
      label: 'Applications',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 12h6m-6 4h6m2 4H7a2 2 0 01-2-2V6a2 2 0 012-2h6l6 6v10a2 2 0 01-2 2z" />
        </svg>
      ),
      hint: 'Rental & KYC',
      badge: pendingKycApplications
    },
    {
      key: 'email',
      label: 'Email',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8m-18 8h18a2 2 0 002-2V8a2 2 0 00-2-2H3a2 2 0 00-2 2v6a2 2 0 002 2z" />
        </svg>
      ),
      hint: 'Templates & blasts'
    },
    {
      key: 'analytics',
      label: 'Analytics',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M11 11V5a1 1 0 012 0v6m5 0V7a1 1 0 012 0v4m-16 0V9a1 1 0 012 0v2m-3 4h18" />
        </svg>
      ),
      hint: 'Growth & trends'
    },
    {
      key: 'transactions',
      label: 'Transactions',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      hint: 'Payments & payouts'
    },
    {
      key: 'commission',
      label: 'Commission',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M17 8.5A3.5 3.5 0 1010 6m4.5 12.5A3.5 3.5 0 109 16" />
        </svg>
      ),
      hint: 'Platform earnings'
    },
    {
      key: 'payouts',
      label: 'Payouts',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
      hint: 'Landlord payouts'
    },
    {
      key: 'disputes',
      label: 'Disputes',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M18.364 5.636a9 9 0 010 12.728M15.536 8.464a5 5 0 010 7.072M8.464 8.464a5 5 0 000 7.072M5.636 5.636a9 9 0 000 12.728M9 12h6" />
        </svg>
      ),
      hint: 'Resolve issues'
    },
    {
      key: 'moderation',
      label: 'Content & Violations',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M4 6h16M4 10h16M4 14h10m-6 4h6" />
        </svg>
      ),
      hint: 'Trust & safety'
    },
    {
      key: 'kyc',
      label: 'KYC Verification',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 12c2.761 0 5-2.239 5-5S14.761 2 12 2 7 4.239 7 7s2.239 5 5 5zM4 22a8 8 0 1116 0H4z" />
        </svg>
      ),
      hint: 'Identity checks',
      badge: pendingKycApplications
    },
    {
      key: 'settings',
      label: 'Settings',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M10.325 4.317l.426-1.315A1 1 0 0111.708 2h.584a1 1 0 01.957.668l.426 1.315a1 1 0 00.95.69h1.385a1 1 0 01.95.69l.426 1.316a1 1 0 01-.24 1.026l-.987.987a1 1 0 000 1.414l.987.987a1 1 0 01.24 1.026l-.426 1.316a1 1 0 01-.95.69h-1.385a1 1 0 00-.95.69l-.426 1.315a1 1 0 01-.957.669h-.584a1 1 0 01-.957-.669l-.426-1.315a1 1 0 00-.95-.69H7.99a1 1 0 01-.95-.69l-.426-1.316a1 1 0 01.24-1.026l.987-.987a1 1 0 000-1.414l-.987-.987a1 1 0 01-.24-1.026l.426-1.316A1 1 0 017.99 5.006h1.385a1 1 0 00.95-.69z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      hint: 'Platform controls'
    },
    {
      key: 'audit',
      label: 'Audit Logs',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      hint: 'Track actions'
    },
    {
      key: 'bulk',
      label: 'Bulk Ops',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M4 7h16M4 12h8m-8 5h16" />
        </svg>
      ),
      hint: 'Save time'
    },
    {
      key: 'notifications',
      label: 'Notifications',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6 6 0 10-12 0v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
      ),
      hint: 'System alerts'
    },
    {
      key: 'reviews',
      label: 'Reviews',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
        </svg>
      ),
      hint: 'Moderate reviews'
    }
  ]

  const activeTabMeta = tabItems.find((tab) => tab.key === activeTab)

  const StatCard = ({
    title,
    value,
    badge,
    helper,
    tone = 'emerald'
  }: {
    title: string
    value: ReactNode
    badge?: string
    helper?: string
    tone?: 'emerald' | 'amber' | 'blue' | 'stone' | 'purple'
  }) => {
    const toneMap: Record<typeof tone, string> = {
      emerald: 'bg-emerald-50 text-emerald-700',
      amber: 'bg-amber-50 text-amber-700',
      blue: 'bg-blue-50 text-blue-700',
      stone: 'bg-stone-100 text-stone-700',
      purple: 'bg-purple-50 text-purple-700'
    }

    return (
      <div className="card card-hover p-5 border border-white/60 shadow-lg shadow-emerald-100/40">
        <p className="text-sm text-neutral-500">{title}</p>
        <div className="flex items-center gap-2 mt-2">
          <span className="text-3xl font-bold text-neutral-900 font-display">{value}</span>
          {badge && <span className={`text-xs font-semibold px-2 py-1 rounded-full ${toneMap[tone]}`}>{badge}</span>}
        </div>
        {helper && <p className="text-sm text-neutral-500 mt-2">{helper}</p>}
      </div>
    )
  }

  if (loading) {
    return (
      <div className="relative min-h-screen flex items-center justify-center bg-neutral-50 overflow-hidden">
        <div className="absolute inset-0 mesh-gradient opacity-90"></div>
        <div className="relative bg-white/80 backdrop-blur-xl border border-white/60 shadow-2xl rounded-3xl px-10 py-12 text-center">
          <div className="w-14 h-14 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin mx-auto"></div>
          <p className="mt-6 text-neutral-700 font-medium">Loading the admin workspace...</p>
        </div>
      </div>
    )
  }

  if (dataError) {
    return (
      <div className="relative min-h-screen flex items-center justify-center bg-neutral-50 overflow-hidden">
        <div className="absolute inset-0 mesh-gradient opacity-90"></div>
        <div className="relative bg-white/85 backdrop-blur-xl border border-red-100 shadow-2xl rounded-3xl px-10 py-12 text-center">
          <div className="text-red-600 text-3xl mb-3">⚠️</div>
          <h2 className="text-2xl font-display text-neutral-900 mb-2">Connection issue</h2>
          <p className="text-neutral-600 mb-4">{dataError}</p>
          <button
            onClick={() => window.location.reload()}
            className="btn-primary px-5 py-3"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  if (!user) {
    return null
  }

  return (
    <div className="relative min-h-screen bg-neutral-50">
      <div className="absolute inset-0 pointer-events-none">
        <div className="mesh-gradient absolute inset-0 opacity-80"></div>
        <div className="absolute -top-32 right-12 w-96 h-96 bg-emerald-200/40 blur-3xl rounded-full"></div>
        <div className="absolute bottom-0 left-8 w-80 h-80 bg-amber-200/35 blur-3xl rounded-full"></div>
      </div>

      <div className="relative flex min-h-screen">
        {/* Sidebar */}
        <aside className="hidden lg:flex lg:sticky lg:top-0 lg:h-screen w-72 flex-shrink-0 flex-col border-r border-white/50 bg-white/70 backdrop-blur-xl shadow-lg shadow-emerald-100/50">
          <div className="px-6 py-6 border-b border-white/60 flex-shrink-0">
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="LandlordNoAgent Logo" className="w-16 h-16 object-contain" />
              <div>
                <p className="text-xs uppercase tracking-wide text-emerald-700 font-semibold">LandlordNoAgent</p>
                <p className="text-lg font-display text-neutral-900 leading-tight">Admin Control</p>
              </div>
            </div>
            <p className="text-xs text-neutral-500 mt-3">Consistent with the landing brand—emerald, warm neutrals, and glass panels.</p>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4 space-y-2">
            {tabItems.map(({ key, label, icon, hint, badge }) => (
              <button
                key={key}
                type="button"
                onClick={() => setActiveTab(key)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 ${
                  activeTab === key
                    ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-200/50'
                    : 'text-neutral-700 hover:bg-white/70'
                }`}
              >
                <span className={`flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center ${activeTab === key ? 'bg-white/15' : 'bg-emerald-50 text-emerald-700'}`}>
                  {icon}
                </span>
                <div className="flex-1 text-left min-w-0">
                  <div className="text-sm font-semibold leading-tight">{label}</div>
                  {hint && <p className={`text-xs ${activeTab === key ? 'text-emerald-100' : 'text-neutral-500'} leading-tight`}>{hint}</p>}
                </div>
                {badge !== undefined && badge > 0 && (
                  <span className={`flex-shrink-0 text-xs font-semibold px-2 py-1 rounded-full ${activeTab === key ? 'bg-white/20 text-white' : 'bg-amber-100 text-amber-700'}`}>
                    {badge}
                  </span>
                )}
              </button>
            ))}
          </div>

          <div className="px-4 py-5 border-t border-white/60 flex-shrink-0">
            <button
              onClick={handleLogout}
              className="w-full btn-secondary text-sm flex items-center justify-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M15 12H3m0 0l4-4m-4 4l4 4m6-4h6m0 0V6m0 6v6" />
              </svg>
              Logout
            </button>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 px-4 sm:px-6 lg:px-10 py-8 space-y-6">
          <div className="bg-white/90 backdrop-blur-xl border border-white/60 rounded-3xl shadow-xl shadow-emerald-100/40 p-6 lg:p-8">
            <div className="flex flex-wrap items-center justify-between gap-4 border-b border-stone-100 pb-4">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setMobileNavOpen(true)}
                  className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-xl border border-neutral-200 bg-white text-neutral-700 hover:border-emerald-300 hover:text-emerald-600 transition"
                  aria-label="Open navigation"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
                <p className="text-xs font-semibold uppercase tracking-[0.15em] text-emerald-600">Platform Management</p>
              </div>
              <div className="w-full lg:w-auto">
                <h1 className="text-3xl font-display text-neutral-900 leading-tight">{activeTabMeta?.label ?? 'Overview'}</h1>

              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <p className="text-xs text-neutral-500">Signed in</p>
                  <p className="font-semibold text-neutral-800">{user.email}</p>
                </div>
                <button
                  onClick={handleLogout}
                  className="btn-secondary text-sm px-4 py-2"
                >
                  Logout
                </button>
              </div>
            </div>

            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-8 pt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
                  <StatCard
                    title="Total Users"
                    value={totalUsers}
                    badge="Platform"
                    helper={`${landlords} landlords • ${clients} clients`}
                    tone="blue"
                  />
                  <StatCard
                    title="Properties Listed"
                    value={totalProperties}
                    badge={`${verifiedProperties} verified`}
                    helper={`${pendingProperties} pending review`}
                    tone="emerald"
                  />
                  <StatCard
                    title="KYC Applications"
                    value={kycApplications}
                    badge={`${approvedKycApplications} approved`}
                    helper={`${pendingKycApplications} awaiting review`}
                    tone="purple"
                  />
                  <StatCard
                    title="Platform Revenue"
                    value={formatMoneyShort(platformCommission)}
                    helper="Commission earned"
                    tone="amber"
                  />
                  <StatCard
                    title="Total Volume"
                    value={formatMoneyShort(totalRevenue)}
                    helper="Total transaction value"
                    tone="amber"
                  />
                </div>

                {/* Status Tracking */}
                {(pendingKycApplications > 0 || pendingProperties > 0) && (
                  <div className="card card-hover p-6 border border-amber-200 bg-amber-50/50">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-neutral-900">⚠️ Action Required</h3>
                      <span className="badge badge-warning">
                        {pendingKycApplications + pendingProperties} pending
                      </span>
                    </div>
                    <div className="space-y-3">
                      {pendingKycApplications > 0 && (
                        <button
                          onClick={() => setActiveTab('kyc')}
                          className="w-full flex items-center justify-between p-3 bg-white rounded-lg border border-amber-200 hover:border-amber-300 hover:shadow-md transition-all"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 12c2.761 0 5-2.239 5-5S14.761 2 12 2 7 4.239 7 7s2.239 5 5 5zM4 22a8 8 0 1116 0H4z" />
                              </svg>
                            </div>
                            <div className="text-left">
                              <p className="font-semibold text-neutral-900">KYC Verifications</p>
                              <p className="text-sm text-neutral-600">{pendingKycApplications} awaiting review</p>
                            </div>
                          </div>
                          <svg className="w-5 h-5 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      )}
                      {pendingProperties > 0 && (
                        <button
                          onClick={() => setActiveTab('properties')}
                          className="w-full flex items-center justify-between p-3 bg-white rounded-lg border border-amber-200 hover:border-amber-300 hover:shadow-md transition-all"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                              <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9.75L12 4l9 5.75M4.5 10.5V20h15v-9.5M9 20v-6h6v6" />
                              </svg>
                            </div>
                            <div className="text-left">
                              <p className="font-semibold text-neutral-900">Property Verifications</p>
                              <p className="text-sm text-neutral-600">{pendingProperties} pending review</p>
                            </div>
                          </div>
                          <svg className="w-5 h-5 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="card card-hover p-6 border border-emerald-50">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-neutral-900">User verification</h3>
                      <span className="badge badge-success">{verificationRate}% rate</span>
                    </div>
                    <div className="space-y-3">
                      <div className="flex justify-between text-sm">
                        <span className="text-neutral-600">Verified users</span>
                        <span className="font-semibold text-emerald-700">{verifiedUsers}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-neutral-600">Pending verification</span>
                        <span className="font-semibold text-amber-700">{unverifiedUsers}</span>
                      </div>
                    </div>
                  </div>

                  <div className="card card-hover p-6 border border-emerald-50">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-neutral-900">Property verification</h3>
                      <span className="badge badge-success">{propertyVerificationRate}% rate</span>
                    </div>
                    <div className="space-y-3">
                      <div className="flex justify-between text-sm">
                        <span className="text-neutral-600">Verified properties</span>
                        <span className="font-semibold text-emerald-700">{verifiedProperties}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-neutral-600">Pending review</span>
                        <span className="font-semibold text-amber-700">{pendingProperties}</span>
                      </div>
                    </div>
                    <div className="mt-4">
                      {pendingProperties > 0 ? (
                        <button
                          onClick={() => setActiveTab('properties')}
                          className="btn-primary w-full text-sm"
                        >
                          Review {pendingProperties} pending properties
                        </button>
                      ) : (
                        <div className="flex items-center gap-2 text-sm text-emerald-700">
                          <span>✅</span>
                          <span>All properties verified</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="card card-hover p-6 border border-white/60">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <p className="text-sm text-neutral-500">KYC pipeline</p>
                        <h3 className="text-lg font-semibold text-neutral-900">Document review</h3>
                      </div>
                      <span className="badge badge-warning">{pendingKycApplications} pending</span>
                    </div>
                    <p className="text-sm text-neutral-600 mb-4">Prioritize outstanding submissions to keep onboarding fast.</p>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="p-3 rounded-xl bg-emerald-50 text-emerald-800 font-semibold">{approvedKycApplications} approved</div>
                      <div className="p-3 rounded-xl bg-amber-50 text-amber-800 font-semibold">{pendingKycApplications} under review</div>
                    </div>
                    <button
                      onClick={() => setActiveTab('applications')}
                      className="btn-secondary w-full mt-4 text-sm"
                    >
                      Go to KYC queue
                    </button>
                  </div>

                  <div className="card card-hover p-6 border border-white/60">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <p className="text-sm text-neutral-500">Actions</p>
                        <h3 className="text-lg font-semibold text-neutral-900">Quick navigation</h3>
                      </div>
                      <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9.75 17L15 12l-5.25-5" />
                      </svg>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        onClick={() => setActiveTab('users')}
                        className="card-hover border border-emerald-100 bg-emerald-50 text-emerald-800 rounded-xl px-4 py-3 text-sm font-semibold"
                      >
                        Manage users
                      </button>
                      <button
                        onClick={() => setActiveTab('transactions')}
                        className="card-hover border border-amber-100 bg-amber-50 text-amber-800 rounded-xl px-4 py-3 text-sm font-semibold"
                      >
                        Payments & commission
                      </button>
                      <button
                        onClick={() => setActiveTab('moderation')}
                        className="card-hover border border-blue-100 bg-blue-50 text-blue-800 rounded-xl px-4 py-3 text-sm font-semibold"
                      >
                        Trust & safety
                      </button>
                      <button
                        onClick={() => setActiveTab('analytics')}
                        className="card-hover border border-neutral-200 bg-white text-neutral-800 rounded-xl px-4 py-3 text-sm font-semibold"
                      >
                        Platform analytics
                      </button>
                    </div>
                  </div>
                </div>

                <div className="card card-hover p-6 border border-white/60">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-neutral-900">Latest KYC applications</h3>
                    <span className="text-sm text-neutral-500">Most recent 5 submissions</span>
                  </div>
                  {users.filter((adminUser: any) => adminUser.kyc_data?.documents && adminUser.role !== 'admin').slice(0, 5).length > 0 ? (
                    <div className="space-y-3">
                      {users
                        .filter((adminUser: any) => adminUser.kyc_data?.documents && adminUser.role !== 'admin')
                        .slice(0, 5)
                        .map((adminUser: any) => (
                          <div key={adminUser._id || adminUser.id} className="flex items-center justify-between p-3 rounded-xl bg-neutral-50">
                            <div>
                              <p className="font-semibold text-neutral-900">
                                {adminUser.kyc_data?.firstName && adminUser.kyc_data?.lastName
                                  ? `${adminUser.kyc_data.firstName} ${adminUser.kyc_data.lastName}`
                                  : adminUser.email}
                              </p>
                              <p className="text-sm text-neutral-500">
                                {adminUser.role} • {Object.keys(adminUser.kyc_data?.documents || {}).length} documents
                              </p>
                            </div>
                            <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                              adminUser.is_verified ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'
                            }`}>
                              {adminUser.is_verified ? 'Verified' : 'Pending'}
                            </span>
                          </div>
                        ))}
                    </div>
                  ) : (
                    <div className="text-center text-neutral-500 py-6">
                      No KYC applications yet
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Users Tab */}
            {activeTab === 'users' && (
              <div className="card card-hover p-4 sm:p-6 mt-6">
                <UserManagement
                  users={users}
                  onUpdate={handleDataUpdate}
                />
              </div>
            )}

            {/* Properties Tab */}
            {activeTab === 'properties' && (
              <div className="card card-hover p-4 sm:p-6 mt-6 w-full overflow-hidden">
                <PropertyManagement
                  properties={properties}
                  onUpdate={handleDataUpdate}
                />
              </div>
            )}

            {/* Applications Tab */}
            {activeTab === 'applications' && (
              <div className="card card-hover p-4 sm:p-6 mt-6 space-y-4">
                <div className="border-b border-neutral-200 pb-3">
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700 text-sm font-semibold">
                    <span className="w-2 h-2 bg-emerald-500 rounded-full"></span>
                    Rental Applications
                  </div>
                  <p className="text-sm text-neutral-500 mt-2">Review and approve tenant submissions.</p>
                </div>
                <RentalApplicationManagement onUpdate={handleDataUpdate} />
              </div>
            )}

            {/* Email Management Tab */}
            {activeTab === 'email' && (
              <div className="card card-hover p-4 sm:p-6 mt-6">
                <EmailManagement />
              </div>
            )}

            {/* Analytics Tab */}
            {activeTab === 'analytics' && (
              <div className="card card-hover p-4 sm:p-6 mt-6 space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-neutral-900">Platform Analytics</h2>
                  <span className="badge badge-info">Live</span>
                </div>
                <PlatformAnalytics
                  users={users}
                  properties={properties}
                  applications={applications}
                  payments={payments}
                />
              </div>
            )}

            {/* Transactions Tab */}
            {activeTab === 'transactions' && (
              <div className="card card-hover p-4 sm:p-6 mt-6 space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-neutral-900">Transaction Management</h2>
                  <span className="badge badge-warning">Revenue</span>
                </div>
                <TransactionManagement />
              </div>
            )}

            {/* Commission Management Tab */}
            {activeTab === 'commission' && (
              <div className="card card-hover p-4 sm:p-6 mt-6">
                <CommissionManagement onUpdate={handleDataUpdate} />
              </div>
            )}

            {/* Payout Management Tab */}
            {activeTab === 'payouts' && (
              <div className="card card-hover p-4 sm:p-6 mt-6">
                <PayoutManagement users={users} onUpdate={handleDataUpdate} />
              </div>
            )}

            {/* Dispute Resolution Tab */}
            {activeTab === 'disputes' && (
              <div className="card card-hover p-4 sm:p-6 mt-6">
                <DisputeResolution onUpdate={handleDataUpdate} />
              </div>
            )}

            {/* Content Moderation Tab */}
            {activeTab === 'moderation' && (
              <div className="space-y-6 mt-6">
                <div className="card card-hover p-4 sm:p-6">
                  <ModerationViolations users={users} />
                </div>
                <div className="card card-hover p-4 sm:p-6">
                  <ContentModeration onUpdate={handleDataUpdate} />
                </div>
              </div>
            )}

            {/* KYC Verification Tab */}
            {activeTab === 'kyc' && (
              <div className="card card-hover p-4 sm:p-6 mt-6">
                <KycVerificationWorkflow onUpdate={handleDataUpdate} />
              </div>
            )}

            {/* Platform Settings Tab */}
            {activeTab === 'settings' && (
              <div className="card card-hover p-4 sm:p-6 mt-6">
                <PlatformSettings onUpdate={handleDataUpdate} />
              </div>
            )}

            {/* Audit Logs Tab */}
            {activeTab === 'audit' && (
              <div className="card card-hover p-4 sm:p-6 mt-6">
                <AuditLogs onUpdate={handleDataUpdate} />
              </div>
            )}

            {/* Bulk Operations Tab */}
            {activeTab === 'bulk' && (
              <div className="card card-hover p-4 sm:p-6 mt-6">
                <BulkOperations onUpdate={handleDataUpdate} />
              </div>
            )}

            {/* Admin Notifications Tab */}
            {activeTab === 'notifications' && (
              <div className="card card-hover p-4 sm:p-6 mt-6">
                <AdminNotificationSystem onUpdate={handleDataUpdate} />
              </div>
            )}

            {/* Reviews Management Tab */}
            {activeTab === 'reviews' && (
              <div className="card card-hover p-4 sm:p-6 mt-6">
                <ReviewsManagement />
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Mobile drawer */}
      {mobileNavOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setMobileNavOpen(false)} />
          <div className="absolute top-0 left-0 bottom-0 w-72 max-w-[80vw] bg-white shadow-2xl border-r border-stone-200 p-4 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <img src="/logo.png" alt="LandlordNoAgent Logo" className="w-14 h-14 object-contain" />
                <div>
                  <p className="text-xs uppercase text-emerald-700 font-semibold">Admin</p>
                  <p className="text-sm font-semibold text-neutral-900">LandlordNoAgent</p>
                </div>
              </div>
              <button
                onClick={() => setMobileNavOpen(false)}
                className="p-2 rounded-lg hover:bg-neutral-100"
                aria-label="Close navigation"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto space-y-2">
              {tabItems.map(({ key, label, hint, badge, icon }) => (
                <button
                  key={key}
                  onClick={() => {
                    setActiveTab(key)
                    setMobileNavOpen(false)
                  }}
                  className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl border text-left transition ${
                    activeTab === key
                      ? 'bg-emerald-600 text-white border-emerald-600 shadow-lg shadow-emerald-200/50'
                      : 'border-stone-200 hover:border-emerald-300 text-neutral-800'
                  }`}
                >
                  <span className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center">
                    {icon}
                  </span>
                  <div className="flex-1">
                    <p className="text-sm font-semibold leading-tight">{label}</p>
                    {hint && <p className={`text-xs ${activeTab === key ? 'text-emerald-100' : 'text-neutral-500'}`}>{hint}</p>}
                  </div>
                  {badge !== undefined && badge > 0 && (
                    <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                      activeTab === key ? 'bg-white/20 text-white' : 'bg-amber-100 text-amber-700'
                    }`}>
                      {badge}
                    </span>
                  )}
                </button>
              ))}
            </div>
            <div className="pt-3 border-t border-stone-200 mt-3">
              <button
                onClick={() => {
                  setMobileNavOpen(false)
                  handleLogout()
                }}
                className="w-full btn-secondary text-sm"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default function AdminDashboard() {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading Admin Dashboard...</p>
          </div>
        </div>
      }
    >
      <AdminDashboardContent />
    </Suspense>
  )
} 

